import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useImageStore } from 'gpcore-apikit/store/pinia/image';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import type { BasicImage, Image } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { computed, ref, shallowRef } from 'vue';

export default () => {
  const imageStore = useImageStore();
  const isLoading = ref(false);
  const data = shallowRef<Array<BasicImage>>([]);
  const projectStore = useProjectStore();
  const currentProject = computed(() => projectStore.current);

  /**
   * Finds a project image from the given source array of images.
   *
   * @param image - image to find
   * @param source - source array of images to search in
   * @returns the found image or null if not found
   */
  const findProjectImageFromSource = (image: BasicImage | Image, source: Array<BasicImage>) => {
    return source.find((img) => img.id === image.id);
  };

  /**
   * Finds a project image from the current state of project images.
   *
   * @param image - image to find
   * @returns the found image or null if not found
   */
  const findProjectImage = (image: BasicImage | Image) => {
    if (image == null) {
      return null;
    }

    return findProjectImageFromSource(image, data.value);
  };

  const { addNotification } = useNotifications();
  const error = ref<Error>(null);
  const fetch = async (onlyAvailable: boolean = false) => {
    isLoading.value = true;
    data.value = [];
    error.value = null;

    try {
      const response = await imageStore.reloadProjectImages(currentProject.value, onlyAvailable);
      data.value = response.images;
    } catch (e) {
      addNotification({
        message: (e as ServiceError).message,
        type: 'danger',
      });
      error.value = e as Error;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    data,
    isLoading,
    fetch,
    error,
    findProjectImage,
    findProjectImageFromSource,
  };
};
